import type { ReactNode } from "react";
import { type Identifier, useTranslate } from "ra-core";
import { Link } from "react-router";

import { cn } from "@/lib/utils";

import { formatTime } from "../zoom_meeting_summaries/formatTime";
import { excerptParts } from "./excerptParts";
import { meetingLink } from "./meetingLink";
import { useTranscriptMatch } from "./useTranscriptMatches";

/**
 * The line under a meeting that matched inside its transcript or a summary:
 * where the words are (the time they were said, or which summary holds them)
 * and the words around them, search terms highlighted.
 *
 * `linkable` makes that time or summary name open the meeting there. The mobile
 * app has no meeting page, so it passes false and shows plain text.
 */
export const TranscriptExcerpt = ({
  meetingId,
  linkable = false,
}: {
  meetingId: Identifier;
  linkable?: boolean;
}) => {
  const translate = useTranslate();
  const match = useTranscriptMatch(meetingId);
  if (!match) return null;

  if (!match.excerpt) {
    return (
      <span className="block text-xs text-muted-foreground italic">
        {translate(
          match.kind === "summary"
            ? "resources.zoom_meetings.transcript_search.found_summary"
            : "resources.zoom_meetings.transcript_search.found",
        )}
      </span>
    );
  }

  const parts = excerptParts(match.excerpt.excerpt);
  const plain = parts.map((p) => p.text).join("");
  const anchorClass = "font-mono text-primary hover:underline mr-1.5";

  let anchor: ReactNode;
  if (match.kind === "summary") {
    const { entry_key, entry_label } = match.excerpt;
    anchor = linkable ? (
      <Link
        to={meetingLink(meetingId, { asset: "summary", summary: entry_key })}
        onClick={(e) => e.stopPropagation()}
        className={cn(anchorClass, "font-sans font-medium")}
        aria-label={translate(
          "resources.zoom_meetings.transcript_search.open_summary",
          { label: entry_label },
        )}
      >
        {entry_label}
      </Link>
    ) : (
      <span className="font-medium mr-1.5">{entry_label}</span>
    );
  } else {
    const { cue_start } = match.excerpt;
    const time = formatTime(cue_start);
    anchor = linkable ? (
      <Link
        to={meetingLink(meetingId, { asset: "transcript", t: cue_start })}
        onClick={(e) => e.stopPropagation()}
        className={anchorClass}
        aria-label={translate(
          "resources.zoom_meetings.transcript_search.said_at",
          { time },
        )}
      >
        {time}
      </Link>
    ) : (
      <span className="font-mono mr-1.5">{time}</span>
    );
  }

  return (
    <span
      className="block text-xs text-muted-foreground whitespace-normal line-clamp-2"
      title={plain}
    >
      {anchor}
      {parts.map((part, i) =>
        part.marked ? (
          <mark
            key={i}
            className="bg-amber-200/70 dark:bg-amber-500/30 text-foreground rounded-sm px-0.5"
          >
            {part.text}
          </mark>
        ) : (
          <span key={i}>{part.text}</span>
        ),
      )}
    </span>
  );
};
